import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DevicesProvider,
  NotificationsPage,
  createDevicesApi,
  type DevicesDeps,
} from '@/features/messaging';
import { createApi } from '@/lib/api/endpoints';
import { staffAppHref } from '@/lib/auth/entry';
import { StaffSignIn } from '@/lib/auth/StaffSignIn';
import { APP_VERSION } from '@/lib/native/version';
import { disablePush, enablePush, pushPermission } from '@/lib/push';
import { safeRedirect } from '@/lib/router/guards';

import { clientFetcher } from './bp03';
import { PhoneOtpForm } from './PhoneOtpForm';
import { MOBILE_NAMESPACE, registerMobileStrings } from './bp11strings';
import { useClientDeps } from './deps';

/**
 * BP-11 §6's four client rows: `/settings/notifications`, `/try-on`,
 * `/update-required`, and `/b/$ref` — the short form of a deep link.
 *
 * Kept in one file for the reason `bp04.tsx` and `bp05.tsx` are: a BP's
 * client routes are its own, and `router.tsx` stays a list of paths rather
 * than a place where four BPs' imports meet.
 */

registerMobileStrings();

/** §6's `/settings/notifications`, wrapped in the feature's provider. */
export function NotificationsRoute(): JSX.Element {
  const deps = useClientDeps();
  const devices: DevicesDeps = useMemo(
    () => ({
      api: createDevicesApi(clientFetcher(deps.fetcher)),
      // Passed, not wrapped: each already takes its plugin as an optional
      // argument defaulting to the real one, and a `() => f()` here would
      // be three functions whose only behaviour is to call another.
      permission: pushPermission,
      enable: enablePush,
      disable: disablePush,
    }),
    [deps.fetcher],
  );
  return (
    <DevicesProvider deps={devices}>
      <NotificationsPage />
    </DevicesProvider>
  );
}

/**
 * R9's short link, `https://26ad.in/b/<ref>`.
 *
 * A redirect and nothing else: `/b/<ref>` is `/booking/<ref>`, and a tapped
 * notification lands exactly where a tapped link inside the app does.
 * `<ref>` is BP-04's human reference (`{outlet.code}-{seq:06d}`), never the
 * UUID — the half of R9 that matters for privacy: a reference is guessable
 * only within one outlet's sequence, and the screen behind it still requires
 * a session.
 *
 * **R9's "requires login; after login returns to the link".** The guard asks
 * `GET /me` first, because that is the only way a browser can find out
 * whether its cookie is still good, and sends an unauthenticated tap to
 * `/?next=/booking/<ref>` — the landing page, which is where a customer signs in — instead of to a screen that would show it an
 * error. The round-trip is the point: a notification tapped a week later,
 * with a session that has since idled out (BP-01 R5), is the common case
 * rather than the edge one.
 *
 * The check lives here and not on BP-04's booking screen deliberately. That
 * screen is reached from inside the app, where a session already exists; the
 * deep link is the one entry point that can arrive without one, and putting
 * the redirect anywhere else would make every other screen pay for it.
 */
export function DeepLinkGuard(): JSX.Element {
  const { t } = useTranslation(MOBILE_NAMESPACE);
  const params: { ref?: string } = useParams({ strict: false });
  const deps = useClientDeps();
  const reference = params.ref;
  const api = useMemo(() => createApi(clientFetcher(deps.fetcher)), [deps.fetcher]);
  const session = useQuery({ queryKey: SESSION_QUERY_KEY, queryFn: () => api.me(), retry: false });

  // A bare `/b` is a share link somebody truncated. Their own list is the
  // nearest useful place — and it is behind the same session, so it goes
  // through the same check.
  const destination = reference === undefined ? '/me/bookings' : `/booking/${reference}`;

  if (session.isPending) {
    return (
      <p role="status" data-testid="deep-link-opening" className="text-sm text-muted-foreground">
        {t('deepLink.opening')}
      </p>
    );
  }
  if (session.isError) {
    return <Navigate to="/" search={{ next: destination }} replace />;
  }
  if (reference === undefined) {
    return <Navigate to="/me/bookings" replace />;
  }
  return <Navigate to="/booking/$bookingRef" params={{ bookingRef: reference }} replace />;
}

/** `GET /me`, which is how a browser asks whether its cookie still works. */
export const SESSION_QUERY_KEY = ['client', 'me'] as const;

/**
 * The customer's sign-in, on the landing page, with R9's return path.
 *
 * Customers sign in at the address itself, not on a page of their own: the landing page asks
 * `GET /me` and, when nobody is signed in, shows the phone-and-code form under "Book now".
 * `PhoneOtpForm` is BP-01's screen and stays so; what this adds is where to go afterwards.
 * `next` is read loosely (`strict: false`) and only ever used as a path within this app —
 * `navigate({ to })` cannot leave the origin, so a crafted `?next=https://…` is a 404 here
 * rather than an open redirect.
 */
export function CustomerSignIn(): JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search: { next?: string } = useSearch({ strict: false });
  const next = search.next ?? DEFAULT_AFTER_LOGIN;
  const deps = useClientDeps();
  const api = useMemo(() => createApi(clientFetcher(deps.fetcher)), [deps.fetcher]);
  const session = useQuery({ queryKey: SESSION_QUERY_KEY, queryFn: () => api.me(), retry: false });
  if (!session.isError) {
    return null;
  }
  return (
    <PhoneOtpForm
      headingLevel="h2"
      onSignedIn={() => {
        void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
        void navigate({ to: next, replace: true });
      }}
    />
  );
}

/**
 * `/login`: the one sign-in for everyone who works in the salon.
 *
 * Staff and admins are one kind of account whose roles decide what it can open, so there is
 * one form and no "which door" question. It signs in over the api, which sets the `sid`
 * cookie, then leaves for the staff app with a full page load: the staff app is a different
 * bundle, and its first act is to restore its session from that cookie. `redirect` is the
 * route the staff app sent them from, relative to it; `safeRedirect` refuses anything that
 * is not a path of this origin.
 */
export function AdminLoginRoute(): JSX.Element {
  const search: { redirect?: string } = useSearch({ strict: false });
  const deps = useClientDeps();
  const api = useMemo(() => createApi(clientFetcher(deps.fetcher)), [deps.fetcher]);
  const target = staffAppHref(safeRedirect(search.redirect));
  return (
    <StaffSignIn
      signIn={async (credentials) => {
        await api.login(credentials);
      }}
      onSignedIn={() => {
        pageLoad.go(target);
      }}
    />
  );
}

/**
 * The one place this app asks the browser to load a different page — the staff app is its own
 * bundle, so reaching it is a navigation, not a route change. An object so a test can see the
 * request: jsdom cannot navigate and does not let `window.location.assign` be replaced.
 */
export const pageLoad = {
  go(href: string): void {
    window.location.assign(href);
  },
};

/** Where a client lands when they signed in without a link to return to. */
export const DEFAULT_AFTER_LOGIN = '/me/bookings';

/**
 * §6's `/update-required`, shown on rule R3's 426.
 *
 * No "try again" button. The api has said this build is below
 * `mobile.min_supported_version` and will say it again to every request;
 * the only thing that helps is the store, and a retry that fails the same
 * way is how a person decides the app is broken rather than old.
 */
export function UpdateRequiredRoute(): JSX.Element {
  const { t } = useTranslation(MOBILE_NAMESPACE);
  return (
    <section data-testid="update-required">
      <h1 className="mb-2 text-lg font-semibold">{t('update.title')}</h1>
      <p className="mb-4 text-sm text-muted-foreground">{t('update.body')}</p>
      <p className="mb-4 text-sm text-muted-foreground" data-testid="update-version">
        {t('update.installed', { version: APP_VERSION })}
      </p>
      <div className="flex gap-2">
        <a
          className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground"
          data-testid="update-play"
          href={PLAY_URL}
        >
          {t('update.play')}
        </a>
        <a
          className="rounded border border-border px-3 py-1 text-sm"
          data-testid="update-appstore"
          href={APP_STORE_URL}
        >
          {t('update.appStore')}
        </a>
      </div>
    </section>
  );
}

/** R1's app id, which is also the Play listing's. */
export const PLAY_URL = 'https://play.google.com/store/apps/details?id=in.26ad.client';

/** The App Store listing. `id26ad` is a placeholder until the app is created. */
export const APP_STORE_URL = 'https://apps.apple.com/app/26ad/id26ad';
